import type { UserPropertiesResponse } from "src/shared/api-types/user";

import request from "./request";
import CertificateService from "../services/certificate";

export const getShortUserInfo = async (userId: number) => {
  const res = await request(`/user/${userId}/short`);
  return res;
};

/* Retrieve and validate user certificates */
export const getUserCertificates = async (
  userId: number | null = null,
  crewId: number | null = null,
  fullDescription = false,
  history = false,
  // CREW-602: also declined uploads, with the reviewer's reason (the member's own certificate list).
  includeDeclined = false,
) => {
  let url = "";
  const query: string[] = [];
  if (userId) {
    url = `/user/${userId}/certificates`;
  } else {
    url = `/user/certificates/all`;
  }
  if (crewId) {
    query.push(`crewId=${crewId}`);
  }
  if (fullDescription) {
    query.push(`description=true`);
  }
  if (history) {
    query.push(`history=true`);
  }
  if (includeDeclined) {
    query.push(`includeDeclined=true`);
  }
  if (query.length) {
    url += "?" + query.join("&");
  }

  const json = await request(url);
  if (!json) {
    return {
      certificates: [],
      customCertificates: [],
    };
  }
  if (userId) {
    return CertificateService.map(json);
  }
  return Object.fromEntries(Object.entries(json).map(([k, v]) => [k, CertificateService.map(v)]));
};

export const getUserProperties = async (
  crewId: number,
  userId: number | null = null,
): Promise<UserPropertiesResponse> => {
  let url: string;
  if (userId) {
    url = `/user/${userId}/properties/${crewId}`;
  } else {
    url = `/user/properties/${crewId}/all`;
  }
  return await request(url);
};

export const addUserProperty = async (
  crewId: number,
  userId: number | null,
  property: { roleId?: number; positionId?: number; operationId?: number },
) => {
  let url = "";
  if (userId) {
    url = `/user/${userId}/properties/${crewId}`;
  } else {
    url = `/user/properties/${crewId}`;
  }

  const json = await request.post(url, property);
  return json;
};

export const removeUserProperty = async (
  crewId: number,
  userId: number | null,
  property: { roleId?: number; positionId?: number; operationId?: number },
) => {
  let url = "";
  if (userId) {
    url = `/user/${userId}/properties/${crewId}`;
  } else {
    url = `/user/properties/${crewId}`;
  }
  url += "?";
  if (property.roleId) {
    url += `roleId=${property.roleId}`;
  }
  if (property.positionId) {
    url += `&positionId=${property.positionId}`;
  }
  if (property.operationId) {
    url += `&operationId=${property.operationId}`;
  }

  const response = await request.delete(url);
  return response;
};

export const getUsers = async () => {
  const json = await request(`/user`);
  return json;
};

export const getUserInfo = async (userId: number) => {
  const json = await request(`/user/${userId}`);
  return json;
};

export const changeUserInvoice = async (userId: number, invoice: string) => {
  const json = await request.put(`/user/${userId}/invoice`, { invoice });
  return json;
};

export const getUserBillingAddresses = async () => {
  const addresses = await request(`/user/me/addresses`);
  return addresses;
};

export const getBillingAddress = async (id: number | null) => {
  if (!id) {
    return null;
  }
  const address = await request(`/user/me/addresses/${id}`);
  return address;
};

export const updateBillingAddress = async (id: number, address: object) => {
  const result = await request.put(`/user/me/addresses/${id}`, address);
  return result;
};

export const createBillingAddress = async (address: object) => {
  const result = await request.post(`/user/me/addresses`, address);
  return result;
};

export const deleteBillingAddress = async (id: number) => {
  const result = await request.delete(`/user/me/addresses/${id}`);
  return result;
};
